import { createSlice, createSelector } from '@reduxjs/toolkit';
import { arrayMove } from '@dnd-kit/sortable';
import L from 'leaflet';
import { v4 as uuidv4 } from 'uuid';

import {
  hasMapResponse,
  MapResponseAction,
  RootState,
  LayerType,
  ImageLayerType,
  ExternalLayerType,
  updateSpace,
  SpacePositionType,
  LabelPositionType,
  OverlayType,
} from '../../store';
import { polygonCentroid } from '../../utils/overlays';

type ImagesState = { list: LayerType[]; active: number | null };

// Image holding the spaces/labels/overlays of the active one (duplicates share them)
function ownerImage(state: ImagesState) {
  const activeImg = state.list.find((img) => img.id === state.active);
  if (!activeImg?.duplicateOf) return activeImg;
  return state.list.find((img) => img.id === activeImg.duplicateOf) ?? activeImg;
}

// Keep the space placed at the centroid of the overlay representing it
function syncOverlaySpacePosition(image: LayerType, overlay: OverlayType) {
  if (!overlay.spaceId) return;
  const spacePos = image.spaces.find((sPos) => sPos.space === overlay.spaceId);
  if (spacePos) spacePos.position = polygonCentroid(overlay.points);
}

// Transforms string coordinates to [lat, lng] array.
export function latLngFromDisplayValue(displayCoordinatesValue: string) {
  return displayCoordinatesValue.split(',').map(parseFloat);
}

export const imagesSlice = createSlice({
  name: 'images',
  initialState: {
    list: [] as LayerType[],
    active: null as number | null, // Keep as number for compatibility
  },
  reducers: {
    setActive: (state, action) => {
      if (state.list.find((i) => i.id === action.payload)) {
        state.active = action.payload;
      }
    },

    disable: (state, { payload }) => {
      // Prevent disabling all images
      if (state.list.filter((img) => !img.disabled).length > 1) {
        // Disable all linked layers when disabling the main one
        const linkedLayers = state.list.filter((img) => img.mainLayer === payload);
        linkedLayers.forEach((layer) => {
          state.list.find((img) => img.id === layer.id)!.disabled = true;
        });
        state.list.find((img) => img.id === payload)!.disabled = true;
      }
    },

    enable: (state, { payload }) => {
      const layer = state.list.find((img) => img.id === payload);
      if (!layer) return;
      layer.disabled = false;
      if (layer.mainLayer) {
        const mainLayer = state.list.find((img) => img.id === layer.mainLayer);
        if (mainLayer?.disabled) {
          mainLayer!.disabled = false;
        }
      }
    },

    rename: (state, { payload }) => {
      state.list.find((img) => img.id === payload.id)!.name = payload.name;
    },

    reorder: (state, { payload: { source, destination } }) => {
      const reorderedLayers = arrayMove(
        // Move disabled layers to the end of the array
        state.list.filter((l) => !l.disabled).concat(state.list.filter((l) => l.disabled)),
        source,
        destination
      );
      state.list = reorderedLayers.map((layer, index) => {
        return { ...layer, position_order: index };
      });
    },

    toggle: (state, { payload }) => {
      const layer = state.list.find((layer) => layer.id === payload.id);
      if (!layer) return;
      layer.opened = !payload.opened;
    },

    moveSpaceInImage: (state, action) => {
      const { id, x, y } = action.payload;
      const spacePos = ownerImage(state)!.spaces.find((sPos) => sPos.space === id);
      spacePos!.position = { x, y };
    },

    removeSpaceInImage: (state, action) => {
      const id = action.payload;
      const activeImg = state.list.find((img) => img.id === state.active);
      activeImg!.spaces = activeImg!.spaces.filter((s) => s.space !== id);
      new Set([activeImg, ownerImage(state)]).forEach((image) => image?.overlays?.forEach((overlay) => {
        if (overlay.spaceId === id) overlay.spaceId = null;
      }));
    },

    moveLabelInImage: (state, action) => {
      const { id, x, y } = action.payload;
      const activeImg = state.list.find((img) => img.id === state.active);
      const labelPos = activeImg!.labels.find((lPos) => lPos.label === id);
      if (!labelPos) return;
      labelPos.position = { ...labelPos.position, x, y };
    },

    removeLabelInImage: (state, action) => {
      const id = action.payload;
      const activeImg = state.list.find((img) => img.id === state.active);
      activeImg!.labels = activeImg!.labels.filter((l) => l.label !== id);
      new Set([activeImg, ownerImage(state)]).forEach((image) => image?.overlays?.forEach((overlay) => {
        if (overlay.labelId === id) overlay.labelId = null;
      }));
    },

    rotateLabelInImage: (state, action) => {
      const { id, rotation } = action.payload;
      const activeImg = state.list.find((img) => img.id === state.active);
      const labelPos = activeImg!.labels.find((lPos) => lPos.label === id);
      if (labelPos) {
        if (rotation !== undefined) {
          labelPos.position.rotation = rotation;
        } else {
          delete labelPos.position.rotation;
        }
      }
    },

    // Add a new Space-position pointing to a Space
    // Also allows to change the pointed Space
    addSpaceInImage: (state, { payload }) => {
      const activeImg = state.list.find((img) => img.id === state.active)!;
      const { space, newSpace, lat: x, lng: y } = payload;
      const spacePos = activeImg.spaces.find((sPos) => sPos.space === space);
      if (spacePos && newSpace) {
        // Changing Space-position to point another Space
        spacePos.space = newSpace;
      } else {
        // Adding a Space-position for a new Space
        activeImg.spaces.push({ id: uuidv4(), space, position: { x, y }, layerId: Number(activeImg.id) });
      }
    },

    // Add a new Label-position pointing to a Label
    // Also allows to change the pointed Label
    addLabelInImage: (state, { payload }) => {
      const activeImg = state.list.find((img) => img.id === state.active)!;
      const { label, newLabel, lat: x, lng: y, rotation } = payload;
      const labelPos = activeImg.labels.find((lPos) => lPos.label === label);
      if (labelPos && newLabel) {
        // Changing Label-position to point another Label
        labelPos.label = newLabel;
      } else {
        // Adding a Label-position for a new Label
        activeImg.labels.push({ id: uuidv4(), label, position: { x, y, rotation }});
      }
    },

    addOverlayInImage: (state, { payload }: { payload: OverlayType }) => {
      const image = ownerImage(state);
      if (!image) return;
      image.overlays ??= [];
      image.overlays.push(payload);
      syncOverlaySpacePosition(image, payload);
    },

    // Changes points, style, label or space of an overlay
    updateOverlayInImage: (state, { payload }: { payload: { id: string; changes: Partial<Omit<OverlayType, 'id'>> } }) => {
      const image = ownerImage(state);
      const overlay = image?.overlays?.find((o) => o.id === payload.id);
      if (!image || !overlay) return;
      Object.assign(overlay, payload.changes);
      // A label or space is shown by a single overlay per layer
      image.overlays!.forEach((other) => {
        if (other.id === overlay.id) return;
        if (overlay.labelId && other.labelId === overlay.labelId) other.labelId = null;
        if (overlay.spaceId && other.spaceId === overlay.spaceId) other.spaceId = null;
      });
      if (payload.changes.points || payload.changes.spaceId) {
        syncOverlaySpacePosition(image, overlay);
      }
    },

    removeOverlayInImage: (state, { payload: id }: { payload: string }) => {
      const image = ownerImage(state);
      if (!image?.overlays) return;
      image.overlays = image.overlays.filter((o) => o.id !== id);
    },

    // Used for ExternalLayers, to save current view as default one
    saveCurrentView: (state, { payload }) => {
      const activeImg = state.list.find(
        (img) => img.id === state.active && img.type === 'External'
      ) as ExternalLayerType;
      if (!activeImg) return;
      activeImg.savedView = payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(updateSpace, (state, { payload }) => {
      const { id, coordinates } = payload;
      if (!coordinates) return;
      const [x, y] = latLngFromDisplayValue(coordinates);
      const activeImg = state.list.find((img) => img.id === state.active);
      const spacePos = activeImg!.spaces.find((sPos) => sPos.space === id);
      if (!spacePos) return;
      spacePos.position = { x, y };
    });
    builder.addMatcher(
      (action): action is MapResponseAction => hasMapResponse(action),
      (state, { payload }) => {
        state.list = [...payload.images]
          .sort((a, b) => {
            if (a.position_order === null) return 1;
            if (b.position_order === null) return -1;
            return a.position_order - b.position_order;
          });
        if (payload.preferences?.sidebarOpenItems === 'first') {
          const opened = new Set<number>();
          state.list = state.list.map((img) => {
            if (img.disabled || img.duplicateOf) return img;
            const mainLayerId = img.mainLayer ?? img.id;
            if (opened.has(mainLayerId)) return img;
            opened.add(mainLayerId);
            return { ...img, opened: true };
          });
        } else if (payload.preferences?.sidebarOpenItems === 'all') {
          state.list = state.list.map((img) => ({ ...img, opened: true }));
        }
        const active = state.list.find((img) => img.id === payload.default_image && !img.disabled)
          || state.list.find((img) => !img.disabled && !img.mainLayer);
        state.active = active ? active.id : state.list[0].id;
      }
    );
  },
});

const {
  setActive,
  enable,
  disable,
  rename,
  toggle,
  reorder,
  moveSpaceInImage,
  removeSpaceInImage,
  moveLabelInImage,
  removeLabelInImage,
  addSpaceInImage,
  addLabelInImage,
  addOverlayInImage,
  updateOverlayInImage,
  removeOverlayInImage,
  saveCurrentView,
  rotateLabelInImage,
} = imagesSlice.actions;
export {
  setActive as setActiveImage,
  enable as enableLayer,
  disable as disableLayer,
  rename as renameLayer,
  reorder as reorderLayers,
  toggle as toggleLayer,
  moveSpaceInImage,
  removeSpaceInImage,
  moveLabelInImage,
  removeLabelInImage,
  addSpaceInImage,
  addLabelInImage,
  addOverlayInImage,
  updateOverlayInImage,
  removeOverlayInImage,
  saveCurrentView,
  rotateLabelInImage,
};

export const imageBounds = (map: L.Map, image: ImageLayerType) =>
  new L.LatLngBounds(
    map.unproject([0, 0], map.getMaxZoom()),
    map.unproject([image.width, image.height], map.getMaxZoom()),
  );

// image bounds with extra margin to prevent cutting popups
export const imageBoundsBig = (map: L.Map, image: ImageLayerType) =>
  new L.LatLngBounds(
    map.unproject([-2000, -2000], map.getMaxZoom()),
    map.unproject([image.width + 2000, image.height + 2000], map.getMaxZoom()),
  );

export const externalView = (view:string) => {
  if (!view) {
    // World map, zoomed out and centered
    return [new L.LatLng(30, -25), 3];
  }
  const [lat,lng,zoom] = view.split(',').map(parseFloat);
  return [new L.LatLng(lat, lng), zoom];
};

export const activeImageSelector = createSelector(
  (state: RootState) => state.undoable.present.images.list,
  (state: RootState) => state.undoable.present.images.active,
  (images, active) => images.find((img) => img.id === active),
);

// If the active image is a duplicate, return the duplicated image
export const activeImageOrDuplicateOfSelector = createSelector(
  (state: RootState) => state.undoable.present.images.list,
  activeImageSelector,
  (images, activeImage) => {
    if (!activeImage?.duplicateOf) return activeImage;
    return images.find((img) => img.id === activeImage.duplicateOf) || activeImage;
  }
);

export const enabledImagesSelector = createSelector(
  (state: RootState) => state.undoable.present.images.list,
  (images) =>
    images
      .filter((img) => {
        return !img.disabled;
      })
);

export const disabledImagesSelector = createSelector(
  (state: RootState) => state.undoable.present.images.list,
  (images) => images.filter((img) => img.disabled),
);

// Space positions for current active layer
export const spacePositionsSelector = createSelector(
  activeImageOrDuplicateOfSelector,
  (image) => {
    return (image?.spaces || []).reduce(
      (res, space) => {
        res[space.space.toString()] = {
          x: space.position.x,
          y: space.position.y,
          layerId: space.layerId,
        };
        return res;
      },
      {} as { [spaceId: string]: SpacePositionType },
    );
  }
);

const NO_OVERLAYS: OverlayType[] = [];

// Overlays of the active image (or of the image it duplicates)
export const overlaysSelector = createSelector(
  activeImageOrDuplicateOfSelector,
  (image) => image?.overlays ?? NO_OVERLAYS,
);

export const overlayBySpaceIdSelector = createSelector(
  overlaysSelector,
  (overlays) => overlays.reduce((result, overlay) => {
    if (overlay.spaceId) result[overlay.spaceId] = overlay;
    return result;
  }, {} as Record<string, OverlayType>),
);

export const overlayByLabelIdSelector = createSelector(
  overlaysSelector,
  (overlays) => overlays.reduce((result, overlay) => {
    if (overlay.labelId) result[overlay.labelId] = overlay;
    return result;
  }, {} as Record<string, OverlayType>),
);

// Labels shown by overlays of any layer
export const overlayLabelIdsSelector = createSelector(
  (state: RootState) => state.undoable.present.images.list,
  (images) => new Set(images.flatMap((image) => (image.overlays ?? []).flatMap((overlay) => overlay.labelId ?? []))),
);

// Map placement UUIDs to space IDs (for fixing path nodes after backend load)
// The backend may store the placement id instead of the space id
export const placementIdToSpaceIdSelector = createSelector(activeImageSelector, (image) =>
  (image?.spaces || []).reduce(
    (res, space) => {
      // Map the placement's UUID to the space's ID
      res[space.id] = space.space.toString();
      return res;
    },
    {} as { [placementId: string]: string },
  ),
);

// Get all Layers linked to active Layer
export const linkedLayersSelector = createSelector(
  enabledImagesSelector,
  activeImageOrDuplicateOfSelector,
  (images, activeImage) => {
    if (!activeImage) return [];
    if (activeImage.type !== 'Image') return [activeImage];
    // Main layer, the one we show in view mode
    const mainLayer = activeImage.mainLayer
      ? images.find((img) => img.id === activeImage.mainLayer)!
      : activeImage;
    // Prevent crash when disabling main layer while dependent is the active
    if (!mainLayer) return [activeImage];
    // Dependent layers, same as main but with buildings cut
    const dependentLayers = images.filter((i) => i.mainLayer === mainLayer.id);
    return [ mainLayer, ...dependentLayers ].sort((a,b) => {
      if (a.position_order === null) return 1;
      if (b.position_order === null) return -1;
      return a.position_order - b.position_order;
    }) as ImageLayerType[];
  }
);

// Space positions for current active layer and its linked layers
// (main layer + dependent layers)
export const spacePositionsInAllLinkedLayersSelector = createSelector(
  enabledImagesSelector,
  activeImageOrDuplicateOfSelector,
  linkedLayersSelector,
  (_images, activeImage, linkedLayers) => {
    if (!activeImage) return {};
    // Find all spaces placed in mainLayer + dependentLayers
    const spaces = linkedLayers.map((layer) => layer.spaces).flat();
    return spaces.reduce(
      (res, space) => {
        if (
          // Add the space if it's not yet added
          !res[space.space.toString()]
            // if it's already added, override if it's from activeImage
            || space.layerId === activeImage.id
        ) {
          res[space.space.toString()] = {
            x: space.position.x,
            y: space.position.y,
            layerId: space.layerId,
          };
        }
        return res;
      },
      {} as { [spaceId: string]: { x: number; y: number; layerId: number; } },
    );
  }
);

export const labelPositionsSelector = createSelector(
  activeImageOrDuplicateOfSelector,
  (image) =>
    (image?.labels || []).reduce(
      (res, label) => {
        res[label.label.toString()] = label.position;
        return res;
      },
      {} as { [labelId: string]: LabelPositionType },
    ),
);

export const hasMapboxLayerSelector = createSelector(
  (state: RootState) => state.undoable.present.images.list,
  (images) => {
    return images.filter((img) => {
      return img.type === 'External' &&
        (img as ExternalLayerType).provider === 'mapbox';
    }).length > 0;
  },
);

export const savedCoordinatesSelector = createSelector(
  (state: RootState) => state.undoable.present.images.list,
  (images) => {
    const external = images.find((img) => img.type === 'External');
    if (external?.savedView) {
      const coords = externalView(external.savedView)[0] as L.LatLng;
      return `${coords.lat}, ${coords.lng}`;
    }
    return null;
  },
);
